import { Injectable } from '@angular/core';
import { GigyaService } from './gigya.service';
import { environment } from '../../environments/environment';
import { isNativeAppOidcClient } from './native-app-oidc.util';

export enum Version {
  B2B = 'b2b',
  B2C = 'b2c',
  NSGO = 'nsgo',
}

@Injectable({
  providedIn: 'root'
})
export class VersionService {

  public readonly current: Version = Version.B2B;
  public readonly modernAuthFlow: boolean = false;
  public readonly devMode: boolean = false;
  /**
   * True for the native mobile app's OIDC client_id (per-environment, see
   * environment.nativeAppOidcClientId). When true, AppComponent loads
   * gigya.oidc.js immediately instead of the reload-based oidcEnabled=true
   * dance, and ContinueComponent/GigyaService skip the page reloads that
   * dance otherwise requires.
   */
  public readonly nativeAppOidc: boolean = false;

  constructor(
    private gigyaService: GigyaService
  ) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dev') !== null) {
      this.devMode = true;
    }
    if (this.isB2C(params)) {
      this.current = Version.B2C;
    }
    if (this.isNSGO(params)) {
      this.current = Version.NSGO;
    }
    if (this.isModernAuthFlow()) {
      this.modernAuthFlow = true;
    }
    if (isNativeAppOidcClient()) {
      this.nativeAppOidc = true;
    }
    console.info(
      "VersionService resolved - version:", this.current,
      "| devMode:", this.devMode,
      "| modernAuthFlow:", this.modernAuthFlow,
      "| nativeAppOidc:", this.nativeAppOidc,
      "| b2c param:", params.get('b2c'),
      "| nsgo param:", params.get('nsgo'),
      "| context.redirectUri:", this.gigyaService.context?.redirectUri,
    );
  }

  private isB2C(params: URLSearchParams) {
    return params.get('b2c') !== null;
  }

  private isNSGO(params: URLSearchParams) {
    if (params.get('nsgo') !== null) {
      return true;
    }
    return this.gigyaService.context?.redirectUri.includes('nsgo') ?? false;
  }

  public redirectToFallback(): void {
    console.warn("redirectToFallback CALLED - this is the safety-net fallback path and should be rare. current version:", this.current, "| URL:", window.location.href);
    let url: string | null = null;
    switch (this.current) {
      case Version.B2B: url = environment.fallbackUrlB2B; break;
      case Version.B2C: url = environment.fallbackUrlB2C; break;
      case Version.NSGO: url = environment.fallbackNsgo; break;
      default:
        console.error("redirectToFallback - unknown version, cannot determine fallback URL. current:", this.current);
        return;
    }
    if (this.devMode) {
      console.warn("redirectToFallback - dev mode enabled, suppressing redirect to fallback URL", url);
      return;
    }
    if (url) {
      console.warn("redirectToFallback - redirecting to fallback URL:", url);
      window.location.href = url;
    } else {
      console.warn("redirectToFallback - no fallback URL configured for version", this.current, "- nothing to redirect to");
    }
  }

  private isModernAuthFlow() {
    const params = new URLSearchParams(window.location.search);
    return params.get('modernAuthFlow') !== null || params.get('client_id') === 'RktvLdy5U-5OzXDS6nJyp6Rb';
  }
}
